/**
 * WorkspaceMatterPanel — collapsible overlay on the right edge of the reading zone.
 * Slides in when a matter-linked conversation is selected.
 * Default: hidden. Auto-opens on matter link; user can close manually.
 *
 * Surpasses Gmail: shows full matter context without leaving the inbox.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  X, ArrowSquareOut, Gavel, ListChecks,
  Money, FolderOpen, CheckCircle, Clock,
} from '@phosphor-icons/react';
import { api } from '@/lib/api';

interface MatterIntel {
  id: string;
  caseNumber: string;
  caseName: string;
  status: string;
  layClient: { firstName: string | null; lastName: string | null; company: string | null } | null;
  instructingSolicitor: { firstName: string | null; lastName: string | null; firmName: string | null } | null;
  leadBarrister: { user: { firstName: string; lastName: string } } | null;
  court: { shortName: string } | null;
  hearings?: Array<{ id: string; hearingDate: string; hearingType: string; status: string }>;
  feeRecords?: Array<{ id: string; totalAmount: string }>;
  tasks?: Array<{ id: string; title: string; status: string; dueDate: string | null }>;
  _count?: { hearings: number; documents: number; feeRecords: number; tasks: number };
}

interface Props {
  matterId: string;
  matterName: string;
  matterNumber: string;
  isOpen: boolean;
  onClose: () => void;
  /** Render as a static column (always-on matter intelligence) rather than a slide-over overlay. */
  inline?: boolean;
  onCreateTask: () => void;
  onCreateHearingNote: () => void;
  onLogTime: () => void;
  onSaveToDocuments: () => void;
  onGenerateFeeEntry: () => void;
}

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  intake:            { bg: '#f1f5f9', text: '#475569', label: 'Intake' },
  active:            { bg: '#dcfce7', text: '#166534', label: 'Active' },
  listed:            { bg: '#dbeafe', text: '#1e40af', label: 'Listed' },
  in_trial:          { bg: '#fef3c7', text: '#92400e', label: 'In Trial' },
  judgment_reserved: { bg: '#ede9fe', text: '#6b21a8', label: 'Judgment' },
  closed_won:        { bg: '#dcfce7', text: '#166534', label: 'Won' },
  closed_settled:    { bg: '#f1f5f9', text: '#475569', label: 'Settled' },
  closed_lost:       { bg: '#fee2e2', text: '#991b1b', label: 'Closed' },
  archived:          { bg: '#f1f5f9', text: '#9ca3af', label: 'Archived' },
};

function clientName(intel: MatterIntel): string {
  const c = intel.layClient;
  if (!c) return '—';
  if (c.company) return c.company;
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
}

function solicitorDisplay(intel: MatterIntel): string {
  const s = intel.instructingSolicitor;
  if (!s) return '—';
  const name = [s.firstName, s.lastName].filter(Boolean).join(' ');
  if (s.firmName && name) return `${name} · ${s.firmName}`;
  if (s.firmName) return s.firmName;
  return name || '—';
}

function hearingCountdown(hearingDate: string): string {
  const d = new Date(hearingDate);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'Past';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `In ${diffDays} days`;
  if (diffDays < 30) return `In ${Math.floor(diffDays / 7)} wk${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
  return new Date(hearingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function upcomingHearings(intel: MatterIntel) {
  if (!intel.hearings?.length) return [];
  return intel.hearings
    .filter((h) => ['scheduled', 'confirmed', 'in_progress'].includes(h.status))
    .sort((a, b) => new Date(a.hearingDate).getTime() - new Date(b.hearingDate).getTime())
    .slice(0, 2);
}

function openTasks(intel: MatterIntel) {
  if (!intel.tasks?.length) return [];
  return intel.tasks
    .filter((t) => t.status !== 'completed' && t.status !== 'cancelled')
    .slice(0, 4);
}

function totalFees(intel: MatterIntel): string {
  if (!intel.feeRecords?.length) return '—';
  const sum = intel.feeRecords.reduce((acc, r) => acc + parseFloat(r.totalAmount || '0'), 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(sum);
}

const font = "'IBM Plex Sans', sans-serif";

const sectionTitle: React.CSSProperties = {
  fontFamily: font, fontSize: 10, fontWeight: 700,
  color: 'var(--text-subtle)', textTransform: 'uppercase',
  letterSpacing: '0.08em', marginBottom: 6, marginTop: 0,
};

const row: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  padding: '5px 0', borderBottom: '1px solid var(--border-subtle)',
  gap: 8,
};

const rowLabel: React.CSSProperties = {
  fontFamily: font, fontSize: 11, color: 'var(--text-muted)', flexShrink: 0,
};

const rowValue: React.CSSProperties = {
  fontFamily: font, fontSize: 11, fontWeight: 500,
  color: 'var(--text-primary)', textAlign: 'right',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130,
};

const actionBtn: React.CSSProperties = {
  width: '100%', textAlign: 'left', background: 'none',
  border: '1px solid var(--border-default)', borderRadius: 5,
  padding: '6px 10px', cursor: 'pointer', fontSize: 11,
  color: 'var(--text-primary)', fontFamily: font,
  display: 'flex', alignItems: 'center', gap: 7,
  marginBottom: 3, transition: 'background 0.1s',
};

export function WorkspaceMatterPanel({
  matterId, matterName, matterNumber, isOpen, onClose, inline = false,
  onCreateTask, onCreateHearingNote, onLogTime, onSaveToDocuments, onGenerateFeeEntry,
}: Props) {
  const navigate = useNavigate();

  const { data: intel, isLoading } = useQuery<MatterIntel>({
    queryKey: ['matter-intel', matterId],
    queryFn: async () => {
      const res = await api.get(`/matters/${matterId}`);
      return res.data.data as MatterIntel;
    },
    staleTime: 60_000,
    enabled: isOpen || inline,
  });

  const statusMeta = intel
    ? (STATUS_COLORS[intel.status] ?? { bg: '#f1f5f9', text: '#475569', label: intel.status })
    : null;

  const nextHearings = intel ? upcomingHearings(intel) : [];
  const tasks = intel ? openTasks(intel) : [];
  const taskCount = intel?._count?.tasks ?? tasks.length;
  const docCount = intel?._count?.documents ?? 0;

  return (
    <div
      style={inline ? {
        // Always-on column (matter intelligence)
        position: 'relative', width: '100%', height: '100%',
        background: 'var(--surface-card)',
        display: 'flex', flexDirection: 'column',
        fontFamily: font, overflow: 'hidden',
      } : {
        position: 'absolute', top: 0, right: 0, bottom: 0,
        width: 260,
        background: 'var(--surface-card)',
        borderLeft: '1px solid var(--border-default)',
        display: 'flex', flexDirection: 'column',
        fontFamily: font,
        transform: isOpen ? 'translateX(0)' : 'translateX(260px)',
        transition: 'transform 0.22s ease',
        zIndex: 20,
        boxShadow: isOpen ? '-4px 0 16px rgba(0,0,0,0.07)' : 'none',
        overflow: 'hidden',
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '10px 12px 10px',
        borderBottom: '1px solid var(--border-default)',
        background: '#0f2342',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 10, color: '#93c5fd', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>
              {matterNumber}
            </div>
            <div style={{
              fontSize: 12, fontWeight: 600, color: 'white', lineHeight: 1.35,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              {matterName}
            </div>
          </div>
          {!inline && (
            <button
              onClick={onClose}
              title="Close context panel"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#93c5fd', padding: 2, borderRadius: 4, flexShrink: 0,
                display: 'flex', alignItems: 'center',
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>
        {statusMeta && (
          <div style={{
            display: 'inline-block', marginTop: 6,
            padding: '2px 7px', borderRadius: 10,
            background: statusMeta.bg, color: statusMeta.text,
            fontSize: 10, fontWeight: 600, fontFamily: font,
          }}>
            {statusMeta.label}
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 16px' }}>
        {isLoading ? (
          <div style={{ color: 'var(--text-subtle)', fontSize: 12, textAlign: 'center', paddingTop: 24 }}>
            Loading matter…
          </div>
        ) : intel ? (
          <>
            {/* Parties */}
            <div style={{ marginBottom: 14 }}>
              <p style={sectionTitle}>Parties</p>
              <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, overflow: 'hidden' }}>
                <div style={row}>
                  <span style={{ ...rowLabel, paddingLeft: 8 }}>Client</span>
                  <span style={{ ...rowValue, paddingRight: 8 }}>{clientName(intel)}</span>
                </div>
                <div style={row}>
                  <span style={{ ...rowLabel, paddingLeft: 8 }}>Solicitor</span>
                  <span style={{ ...rowValue, paddingRight: 8 }}>{solicitorDisplay(intel)}</span>
                </div>
                {intel.leadBarrister && (
                  <div style={{ ...row, borderBottom: 'none' }}>
                    <span style={{ ...rowLabel, paddingLeft: 8 }}>Lead</span>
                    <span style={{ ...rowValue, paddingRight: 8 }}>
                      {intel.leadBarrister.user.firstName} {intel.leadBarrister.user.lastName}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Upcoming hearings */}
            <div style={{ marginBottom: 14 }}>
              <p style={sectionTitle}>Hearings</p>
              {nextHearings.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {nextHearings.map((h) => {
                    const countdown = hearingCountdown(h.hearingDate);
                    const isUrgent = countdown === 'Today' || countdown === 'Tomorrow' || countdown.includes('2 day') || countdown.includes('3 day');
                    return (
                      <div key={h.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 8px', borderRadius: 5,
                        background: isUrgent ? 'rgba(249,115,22,0.15)' : 'var(--surface-subtle)',
                        border: isUrgent ? '1px solid #fed7aa' : '1px solid var(--border-subtle)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                          <Gavel size={11} color={isUrgent ? '#ea580c' : '#6b7280'} />
                          <span style={{
                            fontFamily: font, fontSize: 11,
                            color: isUrgent ? '#9a3412' : 'var(--text-primary)',
                            fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {h.hearingType}
                          </span>
                        </div>
                        <span style={{
                          fontFamily: font, fontSize: 10, fontWeight: 600, flexShrink: 0,
                          color: isUrgent ? '#ea580c' : '#2563EB',
                        }}>
                          {countdown}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontFamily: font, fontSize: 11, color: 'var(--text-subtle)' }}>
                  No upcoming hearings
                </div>
              )}
            </div>

            {/* Open tasks */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <p style={{ ...sectionTitle, marginBottom: 0 }}>Tasks</p>
                {taskCount > 0 && (
                  <span style={{
                    fontFamily: font, fontSize: 10, fontWeight: 700,
                    background: taskCount > 3 ? '#fee2e2' : '#f1f5f9',
                    color: taskCount > 3 ? '#dc2626' : '#6b7280',
                    borderRadius: 10, padding: '1px 6px',
                  }}>
                    {taskCount} open
                  </span>
                )}
              </div>
              {tasks.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {tasks.map((t) => (
                    <div key={t.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 6,
                      padding: '5px 0', borderBottom: '1px solid var(--border-subtle)',
                    }}>
                      <ListChecks size={11} color="#9ca3af" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{
                          fontFamily: font, fontSize: 11, color: 'var(--text-primary)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {t.title}
                        </div>
                        {t.dueDate && (
                          <div style={{ fontFamily: font, fontSize: 10, color: 'var(--text-subtle)', marginTop: 1 }}>
                            Due {new Date(t.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontFamily: font, fontSize: 11, color: 'var(--text-subtle)' }}>
                  No open tasks
                </div>
              )}
            </div>

            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 14 }}>
              {[
                { Icon: FolderOpen, label: 'Documents', value: String(docCount) },
                { Icon: Money, label: 'Fees', value: totalFees(intel) },
              ].map(({ Icon, label, value }) => (
                <div key={label} style={{
                  background: 'var(--surface-subtle)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 5, padding: '7px 8px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                    <Icon size={10} color="#9ca3af" />
                    <span style={{ fontFamily: font, fontSize: 9, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {label}
                    </span>
                  </div>
                  <div style={{ fontFamily: font, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <div style={{ marginBottom: 12 }}>
              <p style={sectionTitle}>Actions</p>
              {[
                { icon: <CheckCircle size={12} />, label: 'Create task', action: onCreateTask },
                { icon: <Gavel size={12} />, label: 'Create hearing note', action: onCreateHearingNote },
                { icon: <Clock size={12} />, label: 'Log time', action: onLogTime },
                { icon: <Money size={12} />, label: 'Generate fee entry', action: onGenerateFeeEntry },
                { icon: <FolderOpen size={12} />, label: 'Save to documents', action: onSaveToDocuments },
              ].map(({ icon, label, action }) => (
                <button
                  key={label}
                  style={actionBtn}
                  onClick={action}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  <span style={{ color: '#6b7280' }}>{icon}</span>
                  {label}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div style={{ color: 'var(--text-subtle)', fontSize: 12, textAlign: 'center', paddingTop: 24 }}>
            Could not load matter data
          </div>
        )}
      </div>

      {/* Footer — open matter */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border-default)', flexShrink: 0 }}>
        <button
          onClick={() => navigate(`/barrister/matters/${matterId}`)}
          style={{
            width: '100%', padding: '8px 0',
            background: '#0f2342', color: 'white',
            border: 'none', borderRadius: 5, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: font,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <ArrowSquareOut size={13} />
          Open Matter
        </button>
      </div>
    </div>
  );
}
